/**
 * Action-tone dispatch (Phase E7).
 *
 * A small typed lookup so any page can go from a semantic action name to
 * the right Button variant / icon color without inventing its own palette.
 * The whole point is that the ERP reads as one consistent surface — a
 * "Post" button in Purchases should look the same as a "Post" button in
 * Sales, and a "Delete" icon in Products should read the same as a
 * "Delete" icon in HR.
 *
 * Prefer `getActionTone(action)` over hard-coded `variant="…"` strings
 * whenever the button's meaning is one of the known verbs — that way we
 * can tune the palette in one place if we ever want to re-brand.
 */
import type { ButtonProps } from './button';

export type ActionName =
  | 'create'      // "New X"                  primary
  | 'edit'        // Edit / update            outline
  | 'delete'      // Delete / remove          destructive tone
  | 'approve'     // Approve / accept         positive
  | 'confirm'     // Confirm                  positive
  | 'reject'      // Reject / decline         destructive tone
  | 'cancel'      // Cancel                   soft warning
  | 'post'        // Post to ledger           positive
  | 'reverse'     // Reverse / void           soft warning
  | 'send'        // Send / email             info
  | 'print'       // Print                    outline
  | 'export'      // Export CSV/XLSX/PDF      outline
  | 'import'      // Import from file         outline
  | 'download'    // Download                 outline
  | 'refresh'     // Refresh cache            info
  | 'sync'        // Sync integration         info
  | 'clone'       // Duplicate                outline
  | 'archive'     // Archive                  soft warning
  | 'restore'     // Restore                  positive
  | 'convert'     // Convert Q -> SO          primary
  | 'pay'         // Record payment           primary
  | 'return'      // Create return / credit   soft warning
  | 'assign'      // Assign / reassign        outline
  | 'schedule'    // Schedule / reschedule    info
  | 'complete'    // Mark complete            positive
  | 'hold'        // Put on hold              soft warning
  | 'unhold';     // Release hold             positive

export type ActionTone = NonNullable<ButtonProps['variant']>;

/**
 * Map an action verb to a Button variant. The mapping is intentionally
 * tight and short — every entry corresponds to a real UX pattern in the
 * app, not an aspirational one.
 */
export function getActionTone(action: ActionName): ActionTone {
  switch (action) {
    /* CTAs — filled primary */
    case 'create':
    case 'convert':
    case 'pay':
      return 'default';

    /* Positive / confirming actions — soft emerald */
    case 'approve':
    case 'confirm':
    case 'post':
    case 'restore':
    case 'complete':
    case 'unhold':
      return 'positive';

    /* Informational / neutral-active — soft sky */
    case 'send':
    case 'refresh':
    case 'sync':
    case 'schedule':
      return 'info';

    /* Reversible / caution — soft amber */
    case 'cancel':
    case 'reverse':
    case 'archive':
    case 'return':
    case 'hold':
      return 'softWarning';

    /* Destructive — soft rose */
    case 'delete':
    case 'reject':
      return 'softDestructive';

    /* Neutral / tertiary — plain outline */
    case 'edit':
    case 'print':
    case 'export':
    case 'import':
    case 'download':
    case 'clone':
    case 'assign':
    default:
      return 'outline';
  }
}

/**
 * Semantic Tailwind color class for a stand-alone icon (e.g. inside a
 * DetailPage.Section header, or an EmptyState). Not a Button variant —
 * this is for `<Icon className={getIconTone('positive')} />`.
 */
export type IconTone =
  | 'default'
  | 'positive'
  | 'warning'
  | 'info'
  | 'destructive'
  | 'muted';

export function getIconTone(tone: IconTone): string {
  switch (tone) {
    case 'positive':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'warning':
      return 'text-amber-600 dark:text-amber-400';
    case 'info':
      return 'text-sky-600 dark:text-sky-400';
    case 'destructive':
      return 'text-rose-600 dark:text-rose-400';
    case 'muted':
      return 'text-muted-foreground';
    case 'default':
    default:
      return 'text-primary';
  }
}

/**
 * Section-badge background + icon color for a DetailPage.Section tone.
 * Used by `detail-page.tsx` — pages just pass `tone="positive"` etc.
 */
export function getSectionBadgeClasses(tone: IconTone): string {
  switch (tone) {
    case 'positive':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'warning':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
    case 'info':
      return 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300';
    case 'destructive':
      return 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300';
    case 'muted':
      return 'bg-muted text-muted-foreground';
    case 'default':
    default:
      return 'bg-primary/10 text-primary';
  }
}
